import shaka from 'shaka-player';
import {
  activateShakaCaptionTrack,
  collectSidecarCaptionsFromVideo,
  disableShakaCaptions,
  findShakaCaptionTrack,
  isPlaceholderCaptionTrackSrc,
  registerSidecarCaptionWithShaka,
  resolveCaptionUri,
  type SidecarCaptionTrack,
} from './shakaCaptionTracks';
import { ensureDefaultShakaIconsRegistered } from './shakaIconRegistry';

const COMBINED_LANGUAGE_CAPTIONS_CONTROL = 'language_captions';

type ShakaElementBase = shaka.ui.Element & {
  parent: HTMLElement;
  controls: shaka.ui.Controls;
  player: shaka.Player;
  video: HTMLVideoElement;
  localization: shaka.ui.Localization;
  eventManager: {
    listen: (target: EventTarget, type: string, listener: EventListenerOrEventListenerObject) => void;
    release: () => void;
  };
};

type SettingsMenuInstance = ShakaElementBase & {
  button: HTMLButtonElement;
  menu: HTMLElement;
  backButton: HTMLButtonElement;
  backSpan: HTMLSpanElement;
  nameSpan: HTMLSpanElement;
  currentSelection: HTMLSpanElement;
  icon: HTMLElement;
};

type SettingsMenuCtor = new (
  parent: HTMLElement,
  controls: shaka.ui.Controls,
  iconMaterialName: string,
) => SettingsMenuInstance;

type SubtitleOption = {
  track: shaka.extern.Track;
  sidecar: SidecarCaptionTrack;
};

function getSettingsMenuCtor(): SettingsMenuCtor {
  const uiNamespace = shaka.ui as unknown as { SettingsMenu?: SettingsMenuCtor };
  if (!uiNamespace.SettingsMenu) {
    throw new Error('shaka.ui.SettingsMenu is required for language_captions');
  }
  return uiNamespace.SettingsMenu;
}

function createChosenIndicator(): HTMLElement {
  const icon = document.createElement('i');
  icon.classList.add('material-icons-round', 'shaka-chosen-item');
  icon.textContent = 'done';
  icon.setAttribute('aria-hidden', 'true');
  return icon;
}

function clearElement(el: HTMLElement): void {
  while (el.firstChild) {
    el.removeChild(el.firstChild);
  }
}

function formatLanguageCode(language: string, localization: shaka.ui.Localization): string {
  if (!language) return localization.resolve('UNDETERMINED_LANGUAGE');
  try {
    const display = new Intl.DisplayNames(['en'], { type: 'language' });
    const label = display.of(language);
    if (label) return label;
  } catch {
    /* Intl may be unavailable in some environments */
  }
  return language;
}

function trackRowLabel(track: shaka.extern.Track, localization: shaka.ui.Localization): string {
  if (track.label) return track.label;
  return formatLanguageCode(track.language, localization);
}

function dedupeAudioLanguageTracks(tracks: shaka.extern.Track[]): shaka.extern.Track[] {
  const seen = new Set<string>();
  const result: shaka.extern.Track[] = [];
  for (const track of tracks) {
    const key = [track.language, track.label ?? '', (track.roles ?? []).join(',')].join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(track);
  }
  return result;
}

function getPendingSidecarCaptions(video: HTMLVideoElement, player: shaka.Player): SidecarCaptionTrack[] {
  const sidecars = collectSidecarCaptionsFromVideo(video);
  return sidecars.filter((sidecar) => !findShakaCaptionTrack(player, sidecar));
}

function getSubtitleOptions(video: HTMLVideoElement, player: shaka.Player): SubtitleOption[] {
  const sidecars = collectSidecarCaptionsFromVideo(video);
  const shakaTracks = player.getTextTracks();

  return sidecars
    .map((sidecar) => {
      const track =
        shakaTracks.find(
          (candidate) =>
            candidate.label === sidecar.label &&
            candidate.language === sidecar.language &&
            (candidate.kind ?? 'subtitles') === sidecar.kind,
        ) ?? findShakaCaptionTrack(player, sidecar);
      if (!track) return null;
      return { track, sidecar };
    })
    .filter((option): option is SubtitleOption => option !== null);
}

function getActiveSubtitleOption(player: shaka.Player, options: SubtitleOption[]): SubtitleOption | null {
  if (!player.isTextTrackVisible()) return null;
  const active = player.getTextTracks().find((track) => track.active);
  if (!active) return null;
  return options.find((option) => option.track.id === active.id) ?? null;
}

async function activateCaptionSelection(
  player: shaka.Player,
  video: HTMLVideoElement,
  option: SubtitleOption,
): Promise<void> {
  let shakaTrack = findShakaCaptionTrack(player, option.sidecar) ?? option.track;
  if (!player.getTextTracks().some((track) => track.id === shakaTrack.id)) {
    const registered = await registerSidecarCaptionWithShaka(player, option.sidecar);
    if (registered) shakaTrack = registered;
  }
  if (!shakaTrack || shakaTrack.id < 0) return;
  await activateShakaCaptionTrack(player, video, shakaTrack);
}

function buildMenuRow(
  label: string,
  selected: boolean,
  onSelect: () => void,
  onCloseMenu: () => void,
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.classList.add('shaka-no-propagation');
  const text = document.createElement('span');
  text.textContent = label;
  button.appendChild(text);
  if (selected) {
    button.setAttribute('aria-selected', 'true');
    text.classList.add('shaka-chosen-item');
    button.appendChild(createChosenIndicator());
  }
  button.addEventListener('click', (event) => {
    event.stopPropagation();
    onSelect();
    window.setTimeout(onCloseMenu, 0);
  });
  return button;
}

const ShakaSettingsMenu = getSettingsMenuCtor();

class CombinedLanguageCaptionsMenu extends ShakaSettingsMenu {
  private readonly audioColumn: HTMLElement;
  private readonly audioList: HTMLElement;
  private readonly textList: HTMLElement;
  private readonly audioTitle: HTMLElement;
  private readonly textTitle: HTMLElement;

  constructor(parent: HTMLElement, controls: shaka.ui.Controls) {
    super(parent, controls, 'closed_caption');

    this.button.classList.add('shaka-language-captions-button', 'shaka-tooltip-status', 'shaka-no-propagation');
    this.menu.classList.add('shaka-combined-language-captions', 'shaka-cinema-settings-menu');
    this.backButton.classList.add('shaka-cinema-menu-skip-back');

    const overflowLabel = this.button.querySelector('.shaka-overflow-button-label');
    if (overflowLabel instanceof HTMLElement) {
      overflowLabel.classList.add('shaka-hidden');
    }

    const columns = document.createElement('div');
    columns.className = 'shaka-combined-menu-columns';

    this.audioColumn = document.createElement('div');
    this.audioColumn.className = 'shaka-combined-column shaka-combined-audio';
    this.audioTitle = document.createElement('h3');
    this.audioTitle.className = 'shaka-combined-column-title';
    this.audioList = document.createElement('div');
    this.audioList.className = 'shaka-combined-column-list';
    this.audioColumn.append(this.audioTitle, this.audioList);

    const textColumn = document.createElement('div');
    textColumn.className = 'shaka-combined-column shaka-combined-text';
    this.textTitle = document.createElement('h3');
    this.textTitle.className = 'shaka-combined-column-title';
    this.textList = document.createElement('div');
    this.textList.className = 'shaka-combined-column-list';
    textColumn.append(this.textTitle, this.textList);

    columns.append(this.audioColumn, textColumn);
    this.menu.appendChild(columns);

    this.eventManager.listen(this.localization, 'locale-updated', () => this.updateLabels());
    this.eventManager.listen(this.localization, 'locale-changed', () => this.updateLabels());
    this.eventManager.listen(this.player, 'loading', () => this.refresh());
    this.eventManager.listen(this.player, 'trackschanged', () => this.refresh());
    this.eventManager.listen(this.player, 'variantchanged', () => this.refresh());
    this.eventManager.listen(this.player, 'textchanged', () => this.refresh());
    this.eventManager.listen(this.player, 'texttrackvisibility', () => this.refresh());
    this.eventManager.listen(this.video, 'loadedmetadata', () => this.refresh());
    this.eventManager.listen(this.button, 'click', () => {
      queueMicrotask(() => this.refresh());
    });
    for (const node of this.video.querySelectorAll('track')) {
      if (node instanceof HTMLTrackElement && !isPlaceholderCaptionTrackSrc(node.src)) {
        this.eventManager.listen(node, 'load', () => this.refresh());
      }
    }

    this.updateLabels();
    this.refresh();
  }

  private updateLabels(): void {
    this.button.ariaLabel = this.localization.resolve('CAPTIONS');
    this.backButton.ariaLabel = this.localization.resolve('BACK');
    this.nameSpan.textContent = this.localization.resolve('CAPTIONS');
    this.backSpan.textContent = this.localization.resolve('CAPTIONS');
    this.audioTitle.textContent = 'Audio Language';
    this.textTitle.textContent = 'Subtitles';
  }

  private refresh(): void {
    const showAudio = this.populateAudio();
    const subtitleOptions = this.populateText();
    this.syncLayout(showAudio);
    this.button.classList.toggle('shaka-hidden', !showAudio && subtitleOptions.length === 0);
  }

  private syncLayout(showAudio: boolean): void {
    this.audioColumn.classList.toggle('shaka-hidden', !showAudio);
    this.menu.classList.toggle('shaka-combined-single-column', !showAudio);
  }

  private populateAudio(): boolean {
    clearElement(this.audioList);
    const tracks = dedupeAudioLanguageTracks(this.player.getVariantTracks());
    const active = tracks.find((track) => track.active);
    if (tracks.length < 2) return false;

    for (const track of tracks) {
      const selected = track === active;
      const label = track.label || formatLanguageCode(track.language, this.localization);
      const row = buildMenuRow(label, selected, () => {
        this.player.selectAudioLanguage(
          track.language,
          track.roles?.[0],
          track.channelsCount ?? undefined,
          0,
          track.audioCodec ?? undefined,
        );
        this.refresh();
      }, () => this.closeMenu());
      this.audioList.appendChild(row);
    }
    return true;
  }

  private populateText(): SubtitleOption[] {
    clearElement(this.textList);

    const sidecars = collectSidecarCaptionsFromVideo(this.video);
    const pendingSidecars = getPendingSidecarCaptions(this.video, this.player);
    const options = getSubtitleOptions(this.video, this.player);
    const captionsOn = this.player.isTextTrackVisible();
    const active = getActiveSubtitleOption(this.player, options);

    const offLabel = this.localization.resolve('OFF');
    const offRow = buildMenuRow(offLabel, !captionsOn, () => {
      void disableShakaCaptions(this.player, this.video).then(() => this.refresh());
    }, () => this.closeMenu());
    this.textList.appendChild(offRow);

    const renderOptions: SubtitleOption[] = [...options];
    for (const sidecar of pendingSidecars) {
      renderOptions.push({
        sidecar,
        track: {
          id: -1,
          active: false,
          type: 'text',
          bandwidth: 0,
          language: sidecar.language,
          label: sidecar.label,
          kind: sidecar.kind,
          mimeType: 'text/vtt',
        } as shaka.extern.Track,
      });
    }

    for (const option of renderOptions) {
      const selected =
        captionsOn &&
        active !== null &&
        resolveCaptionUri(option.sidecar.uri) === resolveCaptionUri(active.sidecar.uri);
      const label = option.track.label || trackRowLabel(option.track, this.localization);
      const row = buildMenuRow(label, selected, () => {
        void activateCaptionSelection(this.player, this.video, option).then(() => this.refresh());
      }, () => this.closeMenu());
      this.textList.appendChild(row);
    }

    return sidecars.length > 0 ? renderOptions : options;
  }

  private closeMenu(): void {
    this.controls.hideSettingsMenus();
    this.controls.getVideoContainer().focus({ preventScroll: true });
  }
}

class CombinedLanguageCaptionsFactory implements shaka.extern.IUIElement.Factory {
  create(rootElement: HTMLElement, controls: shaka.ui.Controls): shaka.extern.IUIElement {
    return new CombinedLanguageCaptionsMenu(rootElement, controls);
  }
}

let registered = false;

export function ensureShakaCombinedLanguageCaptionsRegistered(): void {
  if (registered) return;
  registered = true;
  ensureDefaultShakaIconsRegistered();
  shaka.ui.Controls.registerElement(COMBINED_LANGUAGE_CAPTIONS_CONTROL, new CombinedLanguageCaptionsFactory());
}
